import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useContent } from '../hooks/useContent';
import { useLocale } from '../context/LocaleContext';
import { LOCALES } from '../context/LocaleContext';
import { preloadImagesForSolutionsPath } from '../utils/imagePreloader';
import PreloadZone from './PreloadZone';
import LocaleLink from './LocaleLink';
import { SHOW_LOCALE_SELECTOR, SHOW_NEW_PAGES_IN_NAV, HIDDEN_NAV_PATHS } from '../config/features';

/** Desktop nav shows from this width (px). Below it: burger menu — avoids horizontal phones (~800–930px) using the cramped desktop bar. */
export const NAV_LAYOUT_BREAKPOINT_PX = 1024;

// Animated hamburger icon that morphs to X (industry standard pattern)
const HamburgerIcon = ({ isOpen, reducedMotion, ...props }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <motion.line
      x1="4"
      y1="7"
      x2="20"
      y2="7"
      initial={false}
      animate={{
        rotate: isOpen ? 45 : 0,
        opacity: 1,
      }}
      transition={{ duration: reducedMotion ? 0 : 0.25, ease: [0.4, 0, 0.2, 1] }}
      style={{ transformOrigin: '12px 7px' }}
    />
    <motion.line
      x1="4"
      y1="12"
      x2="20"
      y2="12"
      initial={false}
      animate={{ opacity: isOpen ? 0 : 1 }}
      transition={{ duration: reducedMotion ? 0 : 0.15, ease: [0.4, 0, 0.2, 1] }}
    />
    <motion.line
      x1="4"
      y1="17"
      x2="20"
      y2="17"
      initial={false}
      animate={{
        rotate: isOpen ? -45 : 0,
        opacity: 1,
      }}
      transition={{ duration: reducedMotion ? 0 : 0.25, ease: [0.4, 0, 0.2, 1] }}
      style={{ transformOrigin: '12px 17px' }}
    />
  </svg>
);

// Header Container
const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-bottom: 1px solid rgba(29, 51, 78, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, rgba(191, 25, 34, 0.3), transparent);
  }
`;

const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 24px;
  height: 80px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: flex;
    justify-content: space-between;
    padding: 0 16px;
    height: 70px;
  }
`;

const Logo = styled(LocaleLink)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  grid-column: 1;
  justify-self: start;
  
  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: none;
  }
`;

// Logo component with responsive images
const ResponsiveLogo = ({ alt, ...props }) => (
  <LogoImage
    src="/logo/logo@1x.png"
    srcSet="/logo/logo@1x.png 1x, /logo/logo@2x.png 2x"
    alt={alt}
    width="405"
    height="80"
    loading="eager"
    fetchPriority="high"
    {...props}
  />
);

const LogoImageMobile = styled.img`
  height: 35px;
  width: auto;
  display: none;
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: block;
  }
`;

// Desktop Navigation
const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  grid-column: 2;
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  
  &:hover .dropdown {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const NavLink = styled(LocaleLink)`
  color: #1d334e;
  text-decoration: none;
  font-size: 15px;
  letter-spacing: 0.2px;
  padding: 10px 4px;
  font-weight: 700 !important;
  position: relative;
  transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    color: #BF1922;
  }
  
  &:hover:after {
    width: 100%;
  }
  
  &:focus {
    outline: none;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2.5px;
    background: linear-gradient(to right, #BF1922, #d32f2f);
    border-radius: 2px;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const NavButton = styled.button`
  color: #1d334e;
  font-size: 15px;
  letter-spacing: 0.2px;
  font-weight: 700 !important;
  padding: 10px 4px;
  position: relative;
  transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: none;
  border: none;
  cursor: pointer;
  
  &:hover {
    color: #BF1922;
  }
  
  &:hover:after {
    width: 100%;
  }
  
  &:focus {
    outline: none;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2.5px;
    background: linear-gradient(to right, #BF1922, #d32f2f);
    border-radius: 2px;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border: 1px solid rgba(29, 51, 78, 0.08);
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08);
  min-width: 220px;
  padding: 8px 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  overflow: visible;
`;

const DropdownItem = styled.div`
  position: relative;
  overflow: visible;
  
  &:hover .sub-dropdown {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
  }
`;

const DropdownLink = styled(LocaleLink)`
  display: block;
  padding: 12px 20px;
  color: #1d334e;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  margin: 0 4px;
  
  &:hover {
    color: #BF1922;
  }
`;

const SubDropdown = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border: 1px solid rgba(29, 51, 78, 0.08);
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08);
  min-width: 200px;
  padding: 8px 0;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  overflow: visible;
`;

const SubDropdownLink = styled(LocaleLink)`
  display: block;
  padding: 10px 20px;
  color: rgba(29, 51, 78, 0.75);
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  margin: 0 4px;
  
  &:hover {
    color: #BF1922;
  }
`;

// Mobile Menu Button - 48px min touch target (Apple HIG / Material)
const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 12px;
  color: #1d334e;
  transition: color 0.2s ease, background 0.2s ease, transform 0.15s ease;
  min-width: 48px;
  min-height: 48px;
  border-radius: 12px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  grid-column: 3;
  justify-self: end;
  
  &:hover {
    color: #BF1922;
    background: rgba(191, 25, 34, 0.06);
  }
  
  &:active {
    background: rgba(191, 25, 34, 0.12);
    transform: scale(0.94);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(191, 25, 34, 0.5);
    outline-offset: 2px;
  }
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: auto;
    justify-self: auto;
  }
`;

// Mobile menu backdrop - tap to close, covers full viewport
const MobileMenuBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: rgba(29, 51, 78, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1999;
  touch-action: none;
  
  @media (min-width: ${NAV_LAYOUT_BREAKPOINT_PX}px) {
    display: none !important;
  }
`;

// Mobile menu panel - slides in from right, full height, proper width
// Portal renders to body so we escape any ancestor constraints (backdrop-filter, max-width, etc.)
const MobileMenuPanel = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  max-width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.99), rgba(255, 255, 255, 0.97));
  backdrop-filter: blur(40px) saturate(180%);
  -webkit-backdrop-filter: blur(40px) saturate(180%);
  z-index: 2000;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.12);
  padding-top: env(safe-area-inset-top, 0px);
  padding-right: env(safe-area-inset-right, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  
  @media (min-width: ${NAV_LAYOUT_BREAKPOINT_PX}px) {
    display: none !important;
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  padding-top: max(16px, env(safe-area-inset-top, 0px));
  border-bottom: 1px solid rgba(29, 51, 78, 0.08);
  background: rgba(255, 255, 255, 0.98);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
`;

const MobileMenuList = styled.div`
  padding: 12px 0 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
`;

const MobileMenuItem = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

// 48px min height for touch targets (WCAG 2.5.5)
const MobileMenuLink = styled(LocaleLink)`
  display: block;
  padding: 16px 24px;
  min-height: 48px;
  color: #1d334e;
  text-decoration: none;
  font-size: 16px;
  transition: background 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: rgba(191, 25, 34, 0.05);
    color: #BF1922;
  }
  
  &:active {
    background: rgba(191, 25, 34, 0.08);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(191, 25, 34, 0.4);
    outline-offset: -2px;
  }
`;

const MobileCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #1d334e;
  padding: 10px;
  border-radius: 12px;
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(191, 25, 34, 0.06);
    color: #BF1922;
  }

  &:active {
    transform: scale(0.94);
    background: rgba(191, 25, 34, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(191, 25, 34, 0.5);
    outline-offset: 2px;
  }
`;

const MobileMenuButton2 = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 16px 24px;
  min-height: 48px;
  background: none;
  border: none;
  color: #1d334e;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: rgba(191, 25, 34, 0.05);
    color: #BF1922;
  }
  
  &:active {
    background: rgba(191, 25, 34, 0.08);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(191, 25, 34, 0.4);
    outline-offset: -2px;
  }
  
  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid rgba(29, 51, 78, 0.6);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    margin-left: 12px;
    flex-shrink: 0;
  }
  
  @media (prefers-reduced-motion: reduce) {
    &:after {
      transition: none;
    }
  }
`;

// Submenu with CSS grid for smooth height animation (no magic numbers)
const MobileSubMenu = styled.div`
  display: grid;
  grid-template-rows: ${props => props.isOpen ? '1fr' : '0fr'};
  transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(248, 250, 252, 0.6);
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    grid-template-rows: ${props => props.isOpen ? '1fr' : '0fr'};
  }
`;

const MobileSubMenuInner = styled.div`
  overflow: hidden;
`;

const MobileSubMenuItem = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileSubMenuLink = styled(LocaleLink)`
  display: block;
  padding: 14px 24px 14px 44px;
  min-height: 44px;
  color: rgba(29, 51, 78, 0.85);
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: background 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: rgba(191, 25, 34, 0.04);
    color: #BF1922;
  }
  
  &:active {
    background: rgba(191, 25, 34, 0.06);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(191, 25, 34, 0.4);
    outline-offset: -2px;
  }
`;

const MobileSubSubMenu = styled.div`
  background: rgba(255, 255, 255, 0.6);
  border-left: 3px solid rgba(191, 25, 34, 0.25);
  margin-left: 44px;
`;

const MobileSubSubMenuLink = styled(LocaleLink)`
  display: block;
  padding: 12px 24px 12px 44px;
  min-height: 44px;
  color: rgba(29, 51, 78, 0.75);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: rgba(191, 25, 34, 0.05);
    color: #1d334e;
  }
  
  &:active {
    background: rgba(191, 25, 34, 0.08);
  }
  
  &:focus-visible {
    outline: 2px solid rgba(191, 25, 34, 0.4);
    outline-offset: -2px;
  }
`;

// Right section: LocaleSelector (desktop) + MobileMenuButton (mobile)
const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  grid-column: 3;
  justify-self: end;
`;

// Locale options - add new languages here for scalability
const LOCALE_OPTIONS = [
  { value: LOCALES.EN, label: 'EN' },
  { value: LOCALES.ZH_CN, label: '中文' },
];

// Locale selector - desktop only
const LocaleSelectorDesktop = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: none;
  }
`;

// Styled select matching app design
const LocaleSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 8px 32px 8px 14px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #1d334e;
  background-color: rgba(255, 255, 255, 0.98);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231d334e' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  border: 1px solid rgba(29, 51, 78, 0.12);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    color: #BF1922;
    border-color: rgba(191, 25, 34, 0.2);
    background-color: rgba(255, 255, 255, 0.98);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(191, 25, 34, 0.4);
    box-shadow: 0 0 0 2px rgba(191, 25, 34, 0.08);
  }
`;

// Locale selector for mobile menu
const LocaleSelectorMobile = styled.div`
  display: none;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  align-items: center;
  gap: 12px;
  
  @media (max-width: ${NAV_LAYOUT_BREAKPOINT_PX - 1}px) {
    display: flex;
  }
`;

// Navigation data
const DEFAULT_NAVIGATION = [
  { name: 'About', type: 'link', path: '/about' },
  { name: 'Services', type: 'link', path: '/services' },
  {
    name: 'Strategy 战略', type: 'dropdown', items: [
      { name: 'Execution Governance 执行治理', path: '/execution-governance' },
      { name: 'Strategic Intelligence 战略情报', path: '/strategic-intelligence' },
    ]
  },
  {
    name: 'Sectors 行业', type: 'dropdown', items: [
      { name: 'Aerospace 航空航天', path: '/solutions/aerospace' },
      { name: 'Electrical & Electronics', path: '/solutions/electrical-electronics' },
      { name: 'Energy & Utilities', path: '/solutions/energy-utilities' },
      { name: 'Healthcare & MedTech', path: '/solutions/healthcare' },
      { name: 'Industrial Equipment', path: '/solutions/industrial-equipment' },
      { name: 'Infrastructure & EPC', path: '/solutions/infrastructure-epc' },
      { name: 'Marine & Offshore 海事与海工', path: '/solutions/marine-offshore' },
      { name: 'Materials & SRs 材料与战略资源', path: '/solutions/materials-resources' },
    ]
  },
  { name: 'Contact', type: 'link', path: '/contact' },
];

const LocaleSelector = ({ variant = 'desktop' }) => {
  const { locale, setLocale } = useLocale();
  const isDesktop = variant === 'desktop';

  const handleChange = (e) => {
    const value = e.target.value;
    if (Object.values(LOCALES).includes(value)) {
      setLocale(value);
    }
  };

  const selectElement = (
    <LocaleSelect
      value={locale}
      onChange={handleChange}
      aria-label="Language selection"
    >
      {LOCALE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </LocaleSelect>
  );

  return isDesktop ? (
    <LocaleSelectorDesktop role="group" aria-label="Language selection">
      {selectElement}
    </LocaleSelectorDesktop>
  ) : (
    <LocaleSelectorMobile role="group" aria-label="Language selection">
      {selectElement}
    </LocaleSelectorMobile>
  );
};

const Header = () => {
  const { site, solutions } = useContent();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const menuPanelRef = useRef(null);
  const reducedMotion = useReducedMotion();

  const preloadForPath = useCallback(
    (path) => () => preloadImagesForSolutionsPath(path, solutions),
    [solutions]
  );

  let navigation = Array.isArray(site?.navigation) && site.navigation.length > 0
    ? site.navigation
    : DEFAULT_NAVIGATION;

  if (!SHOW_NEW_PAGES_IN_NAV && HIDDEN_NAV_PATHS.length > 0) {
    const hiddenSet = new Set(HIDDEN_NAV_PATHS);
    navigation = navigation
      .map((item) => {
        if (item.type === 'link') return hiddenSet.has(item.path) ? null : item;
        if (item.items) {
          const filteredItems = item.items
            .filter((sub) => !hiddenSet.has(sub.path))
            .map((sub) =>
              sub.subItems
                ? { ...sub, subItems: sub.subItems.filter((s) => !hiddenSet.has(s.path)) }
                : sub
            );
          return filteredItems.length > 0 ? { ...item, items: filteredItems } : null;
        }
        return item;
      })
      .filter(Boolean);
  }

  const toggleSubMenu = (menuName) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setOpenSubMenus({});
  }, []);

  // Body scroll lock when menu is open
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, scrollY);
    };
  }, [mobileMenuOpen]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen, closeMobileMenu]);

  // Focus trap: focus close button when opening, restore to hamburger when closing
  useEffect(() => {
    if (mobileMenuOpen) {
      closeButtonRef.current?.focus();
    }
  }, [mobileMenuOpen]);

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          <ResponsiveLogo alt={site?.name} />
          <LogoImageMobile src="/logo/symbol.png" alt={site?.name} />
        </Logo>

        <Nav aria-label="Main navigation">
          {navigation.map((item) => (
            <NavItem key={item.name}>
              {item.type === 'link' ? (
                <NavLink to={item.path} aria-label={`Navigate to ${item.name}`}>
                  {item.name}
                </NavLink>
              ) : (
                <>
                  <NavButton
                    aria-haspopup="true"
                    aria-expanded="false"
                    aria-label={`${item.name} menu`}
                  >
                    {item.name}
                  </NavButton>
                  <Dropdown className="dropdown" role="menu" aria-label={`${item.name} submenu`}>
                    {item.items.map((subItem) => (
                      <DropdownItem key={subItem.name} role="none">
                        <PreloadZone preload={preloadForPath(subItem.path)}>
                          <DropdownLink to={subItem.path} role="menuitem">
                            {subItem.name}
                          </DropdownLink>
                        </PreloadZone>
                        {subItem.subItems && (
                          <SubDropdown className="sub-dropdown" role="menu" aria-label={`${subItem.name} submenu`}>
                            {subItem.subItems.map((subSubItem) => (
                              <PreloadZone key={subSubItem.name} preload={preloadForPath(subSubItem.path)}>
                                <SubDropdownLink to={subSubItem.path} role="menuitem">
                                  {subSubItem.name}
                                </SubDropdownLink>
                              </PreloadZone>
                            ))}
                          </SubDropdown>
                        )}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                </>
              )}
            </NavItem>
          ))}
        </Nav>

        <RightSection>
          {SHOW_LOCALE_SELECTOR && <LocaleSelector variant="desktop" />}
          <MobileMenuButton
            ref={menuButtonRef}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu-panel"
            type="button"
          >
            <HamburgerIcon isOpen={mobileMenuOpen} reducedMotion={!!reducedMotion} />
          </MobileMenuButton>
        </RightSection>
      </HeaderContent>

      {ReactDOM.createPortal(
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              <MobileMenuBackdrop
                key="backdrop"
                initial={{ opacity: reducedMotion ? 1 : 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.25, ease: [0.4, 0, 0.2, 1] }}
                onClick={closeMobileMenu}
                aria-hidden="true"
              />
              <MobileMenuPanel
                key="panel"
                id="mobile-menu-panel"
                ref={menuPanelRef}
                initial={{ x: reducedMotion ? 0 : '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: reducedMotion ? 0 : 0.3, ease: [0.4, 0, 0.2, 1] }}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
              >
                <MobileMenuHeader>
                  <Logo to="/" aria-label="Home" onClick={closeMobileMenu}>
                    <ResponsiveLogo alt={site?.name || 'APEXGATE GLOBAL'} />
                    <LogoImageMobile src="/logo/symbol.png" alt={site?.name || 'APEXGATE GLOBAL'} />
                  </Logo>

                  <MobileCloseButton
                    ref={closeButtonRef}
                    type="button"
                    aria-label="Close navigation menu"
                    onClick={closeMobileMenu}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </MobileCloseButton>
                </MobileMenuHeader>
                {SHOW_LOCALE_SELECTOR && <LocaleSelector variant="mobile" />}
                <MobileMenuList role="menu" aria-label="Mobile navigation menu">
                  {navigation.map((item) => (
                    <MobileMenuItem key={item.name} role="none">
                      {item.type === 'link' ? (
                        <MobileMenuLink
                          to={item.path}
                          onClick={closeMobileMenu}
                          role="menuitem"
                        >
                          {item.name}
                        </MobileMenuLink>
                      ) : (
                        <>
                          <MobileMenuButton2
                            isOpen={openSubMenus[item.name]}
                            onClick={() => toggleSubMenu(item.name)}
                            aria-expanded={openSubMenus[item.name] || false}
                            aria-label={`Toggle ${item.name} submenu`}
                            type="button"
                          >
                            {item.name}
                          </MobileMenuButton2>
                          <MobileSubMenu isOpen={openSubMenus[item.name]} role="menu" aria-label={`${item.name} submenu`}>
                            <MobileSubMenuInner>
                              {item.items.map((subItem) => (
                                <MobileSubMenuItem key={subItem.name} role="none">
                                  <PreloadZone preload={preloadForPath(subItem.path)}>
                                    <MobileSubMenuLink
                                      to={subItem.path}
                                      onClick={closeMobileMenu}
                                      role="menuitem"
                                    >
                                      {subItem.name}
                                    </MobileSubMenuLink>
                                  </PreloadZone>
                                  {subItem.subItems && (
                                    <MobileSubSubMenu role="menu" aria-label={`${subItem.name} submenu`}>
                                      {subItem.subItems.map((subSubItem) => (
                                        <PreloadZone key={subSubItem.name} preload={preloadForPath(subSubItem.path)}>
                                          <MobileSubSubMenuLink
                                            to={subSubItem.path}
                                            onClick={closeMobileMenu}
                                            role="menuitem"
                                          >
                                            {subSubItem.name}
                                          </MobileSubSubMenuLink>
                                        </PreloadZone>
                                      ))}
                                    </MobileSubSubMenu>
                                  )}
                                </MobileSubMenuItem>
                              ))}
                            </MobileSubMenuInner>
                          </MobileSubMenu>
                        </>
                      )}
                    </MobileMenuItem>
                  ))}
                </MobileMenuList>
              </MobileMenuPanel>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </HeaderContainer>
  );
};

export default Header;